/**
 * Keyboard Shortcut Handlers
 * 
 * Contains all the handler functions for keyboard shortcuts.
 * Each handler receives the event and any additional context needed.
 */

import { toggleCollapsedNav, onMobileNavToggle } from 'store/slices/themeSlice';
import utils from 'utils';

/**
 * Handler for toggling navigation sidebar
 */
export const toggleNavigation = (event, { dispatch, navCollapsed, mobileNav, screens }) => {
  event.preventDefault();
  
  // Check if we're on mobile
  const breakpoint = utils.getBreakPoint(screens);
  const isMobile = breakpoint.length === 0 ? false : !breakpoint.includes('lg');
  
  if (isMobile) {
    // Toggle mobile navigation
    dispatch(onMobileNavToggle(!mobileNav));
  } else {
    // Toggle desktop sidebar
    dispatch(toggleCollapsedNav(!navCollapsed));
  }
};

/**
 * Handler for showing keyboard shortcuts help
 */
export const showHelp = (event, { setShowHelp }) => {
  event.preventDefault();
  setShowHelp(true);
};

/**
 * Handler for showing navigation search
 */
export const showNavigationSearch = (event, { setShowNavigationSearch }) => {
  event.preventDefault();
  setShowNavigationSearch(true);
};

/**
 * Handler for creating a new chat agent
 */
export const createChatAgent = (event, { navigate, location }) => {
  event.preventDefault();
  
  // Navigate to chat agent creation page using React Router
  if (navigate && location?.pathname !== '/app/chat-agent') {
    navigate('/app/chat-agent');
  }
};

/**
 * Handler for creating a new voice agent  
 */
export const createVoiceAgent = (event, { navigate, location }) => {
  event.preventDefault();
  
  // Navigate to voice agent creation page using React Router
  if (navigate && location?.pathname !== '/app/agent') {
    navigate('/app/agent');
  }
};

/**
 * Handler for uploading documents to knowledge base
 */
export const uploadDocument = (event, { navigate, location }) => {
  event.preventDefault();
  
  // Navigate to knowledge base page for document upload using React Router
  if (navigate && location?.pathname !== '/app/kb') {
    navigate('/app/kb');
  } else if (!navigate || !location) {
    // Fallback to window.location if React Router is not available
    if (window.location.pathname !== '/app/kb') {
      window.location.href = '/app/kb';
    }
  }
};

/**
 * Check if user is currently typing in an input field
 */
export const isUserTyping = (event) => {
  // Check if event.target exists to avoid errors
  if (!event.target) {
    return false;
  }
  
  return event.target.tagName === 'INPUT' || 
         event.target.tagName === 'TEXTAREA' || 
         event.target.contentEditable === 'true';
};

/**
 * Check if event matches shortcut configuration
 */
export const matchesShortcut = (event, shortcut) => {
  // Normalize both event key and shortcut key to lowercase for comparison
  const eventKey = event.key.toLowerCase();
  const shortcutKey = shortcut.key.toLowerCase();
  
  // Skip if user is typing, but allow Navigation and Creation shortcuts
  if (isUserTyping(event) && shortcut.category !== 'Navigation' && shortcut.category !== 'Creation') {
    return false;
  }
  
  // Check key match using normalized keys
  if (eventKey !== shortcutKey) {
    return false;
  }
  
  // Check modifier keys
  for (const modifier of shortcut.modifiers) {
    if (!event[modifier]) {
      return false;
    }
  }
  
  // Ensure no extra modifiers are pressed
  const pressedModifiers = [
    event.ctrlKey && 'ctrlKey',
    event.shiftKey && 'shiftKey',
    event.altKey && 'altKey',
    event.metaKey && 'metaKey'
  ].filter(Boolean);
  
  return pressedModifiers.length === shortcut.modifiers.length;
};